import { Account, Vendor } from "..";
import i18n from "locales/i18n";

// Resolves the language per call, so messages follow the switcher.
const ta = i18n.getFixedT(null, "auth");

export interface RecoveryRedirect {
  search: string;
  message: string;
}

const PAGES_NEEDING_NO_DATA = new Set(["signUpMethod", "emailForm"]);

const PAGES_AFTER_EMAIL_VERIFICATION = new Set([
  "userReviewForm",
  "businessInfoForm",
  "privacyContactForm",
  "businessRegistrationForm",
  "signatureForm",
  "vendorReviewForm",
]);

const PAGES_AFTER_BUSINESS_DOCUMENTS = new Set([
  "signatureForm",
  "vendorReviewForm",
]);

const DOCUMENTS_LOST_MESSAGE = ta("recovery.documents_lost");

const hasDocument = (file: File | null, url: string | null) =>
  Boolean(file || url);

/**
 * Where to send someone whose refresh dropped data the current step depends
 * on (the whole draft, the unsaved password, or uploaded files), or null.
 */
export const getRecoveryRedirect = (
  page: string,
  account: Account,
  vendor: Vendor,
): RecoveryRedirect | null => {
  if (PAGES_NEEDING_NO_DATA.has(page)) {
    return null;
  }

  // Both the email and Google paths set the email before leaving the first steps.
  if (!account.email) {
    return {
      search: "?currentPage=signUpMethod",
      message: ta("recovery.expired"),
    };
  }

  const isEmailSignUp = account.authProvider !== "google";

  if (isEmailSignUp && !account.password) {
    return {
      search: `?${new URLSearchParams({ currentPage: "emailForm", returnTo: page })}`,
      message: ta("recovery.reenter_password"),
    };
  }

  if (
    isEmailSignUp &&
    !account.emailVerified &&
    PAGES_AFTER_EMAIL_VERIFICATION.has(page)
  ) {
    return {
      search: "?currentPage=emailVerificationForm",
      message: ta("recovery.verify_email"),
    };
  }

  if (
    PAGES_AFTER_BUSINESS_DOCUMENTS.has(page) &&
    (!hasDocument(vendor.proofOfInsurance, vendor.proofOfInsuranceURL) ||
      !hasDocument(vendor.businessLicense, vendor.businessLicenseURL))
  ) {
    return {
      search: "?currentPage=businessRegistrationForm",
      message: DOCUMENTS_LOST_MESSAGE,
    };
  }

  if (
    page === "vendorReviewForm" &&
    vendor.offeringEvInstallation === "Yes" &&
    !hasDocument(vendor.electricalLicense, vendor.electricalLicenseURL)
  ) {
    return {
      search: "?currentPage=signatureForm",
      message: DOCUMENTS_LOST_MESSAGE,
    };
  }

  return null;
};
